import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { HeartPulse, Pill, RefreshCw, ShieldAlert, Siren, Stethoscope } from "lucide-react";

import { DecisionsCard, DeskTiles, ViewerNote, type DeskTile } from "../components/dashboard/DeskParts";
import { longDay, shortDay } from "../components/dashboard/deskLogic";
import { ErrorState, LoadingState } from "../components/PageState";
import { ClientRegister, StatusDot, type RegisterColumn } from "../components/register/ClientRegister";
import { priorityTone, viewerNote, visitConfig, visitTone, watchConfig, type SickBayDesk, type TodayVisit, type WatchAlert } from "../components/sickbay/sickBayLogic";
import { api } from "../services/api";

const cap = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/**
 * The sick bay's dashboard: what needs doing today, the visit queue, the
 * learners whose alerts staff must know about, and the last fortnight.
 * Visits, prescriptions and records are kept on the Sick bay page.
 */
export default function SchoolDoctorDashboard() {
  const query = useQuery({
    queryKey: ["sick-bay-desk"],
    queryFn: async () => (await api.get("/dashboard/medical/")).data as SickBayDesk,
    refetchInterval: 60_000,
  });

  if (query.isLoading) return <LoadingState label="Loading the sick bay's dashboard…" />;
  if (query.isError || !query.data) return <ErrorState title="Dashboard unavailable" message="The sick bay's dashboard could not be loaded. Check your connection and try again." retry={() => query.refetch()} />;

  const data = query.data;
  const figures = data.figures;
  const busiest = Math.max(1, ...data.fortnight.days.map((row) => row.visits));
  const fortnightVisits = data.fortnight.days.reduce((sum, row) => sum + row.visits, 0);

  const tiles: DeskTile[] = [
    { label: "Visits today", value: String(figures.visits_today), note: `${figures.seen_today} seen so far`, link: "/medical?view=today", icon: Stethoscope },
    { label: "Emergencies today", value: String(figures.emergencies_today), note: "Emergency or urgent visits", link: "/medical?view=today", icon: Siren, alert: figures.emergencies_today > 0 },
    { label: "On a course of medicine", value: String(figures.on_course), note: "Learners with medicine dispensed", link: "/medical?tab=prescriptions&view=on-course", icon: Pill },
    { label: "Learners with allergies", value: String(figures.with_allergies), note: "Recorded on their health record", link: "/medical?tab=records&view=allergies", icon: ShieldAlert },
  ];

  const visitColumns: RegisterColumn<TodayVisit>[] = [
    { header: "Time", cell: (row) => <span className="tabular-nums">{row.time}</span>, exportValue: (row) => row.time },
    { header: "Learner", cell: (row) => <span><span className="block font-medium text-gray-900">{row.learner}</span><span className="text-xs text-gray-500">{[row.admission_number, row.class_name].filter(Boolean).join(" · ")}</span></span>, exportValue: (row) => row.learner },
    { header: "Class", cell: () => "", exportValue: (row) => row.class_name, exportOnly: true },
    { header: "Visit", cell: (row) => row.visit_type, exportValue: (row) => row.visit_type },
    { header: "Priority", cell: (row) => <span className={priorityTone(row.priority)}>{cap(row.priority)}</span>, exportValue: (row) => cap(row.priority) },
    { header: "Complaint", cell: (row) => <span className="line-clamp-2 min-w-[12rem]">{row.complaint}</span>, exportValue: (row) => row.complaint },
    { header: "Status", cell: (row) => <StatusDot {...visitTone(row)} />, exportValue: (row) => visitTone(row).label },
  ];
  const alertColumns: RegisterColumn<WatchAlert>[] = [
    { header: "Learner", cell: (row) => <span><span className="block font-medium text-gray-900">{row.learner}</span><span className="text-xs text-gray-500">{row.admission_number}</span></span>, exportValue: (row) => row.learner },
    { header: "Class", cell: (row) => row.class_name || "—", exportValue: (row) => row.class_name },
    { header: "Alert", cell: (row) => <span><span className="block text-gray-900">{row.title}</span><span className="text-xs text-gray-500">{row.alert_type}</span></span>, exportValue: (row) => `${row.title} (${row.alert_type})` },
    { header: "What to do", cell: (row) => <span className="line-clamp-2 min-w-[14rem] text-sm">{row.instructions || "—"}</span>, exportValue: (row) => row.instructions },
    { header: "Priority", cell: (row) => <span className={priorityTone(row.priority)}>{cap(row.priority)}</span>, exportValue: (row) => cap(row.priority) },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sick bay</h1>
          <p className="mt-1 text-sm text-gray-500">{longDay(data.today)} · the clinic's day at a glance.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/medical" className="btn btn-primary gap-1.5"><HeartPulse className="h-4 w-4" /> Open the sick bay</Link>
          <button type="button" onClick={() => query.refetch()} className="btn btn-outline gap-2">
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <ViewerNote text={viewerNote(data.viewer_role)} />
      <DecisionsCard decisions={data.work} title="Work waiting" where="in the sick bay" />
      <DeskTiles tiles={tiles} />

      {data.today_visits ? (
        <ClientRegister
          title="Today's visits"
          description="Everyone booked or seen today, most urgent first. Open the Sick bay to start a visit or write it up."
          actions={<Link to="/medical?view=today" className="btn btn-outline h-9 text-sm">Open visits</Link>}
          rows={data.today_visits}
          config={visitConfig}
          columns={visitColumns}
          rowKey={(row) => row.id}
          noun={["visit", "visits"]}
          exportName={{ file: `sick-bay-visits-${data.today}`, title: `Sick bay visits, ${longDay(data.today)}` }}
          emptyText="No one has come to the sick bay today."
        />
      ) : (
        <p className="rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-600">Who was seen, and why, is shown only to staff who may read health records.</p>
      )}

      {data.watch_list && (
        <ClientRegister
          title="Learners to watch"
          description="Serious health alerts in force (high or critical), in class order, with what staff should do. Export it for class teachers and the kitchen."
          rows={data.watch_list}
          config={watchConfig}
          columns={alertColumns}
          rowKey={(row) => row.id}
          noun={["alert", "alerts"]}
          exportName={{ file: `health-alerts-${data.today}`, title: "Learners with serious health alerts" }}
          emptyText="No high or critical health alerts are in force."
        />
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="card">
          <div className="card-titlebar"><div><h2 className="font-semibold text-gray-900">The last two weeks</h2><p className="text-xs text-gray-500">{fortnightVisits} {fortnightVisits === 1 ? "visit" : "visits"}, cancelled ones left out</p></div></div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500"><th className="px-6 py-2 font-medium">Day</th><th className="px-2 py-2 text-right font-medium">Visits</th><th className="px-6 py-2"><span className="sr-only">Share</span></th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {[...data.fortnight.days].reverse().map((row) => (
                <tr key={row.date} className={row.visits ? "" : "text-gray-400"}>
                  <td className="px-6 py-1.5">{shortDay(row.date)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{row.visits}</td>
                  <td className="px-6 py-1.5"><span className="block h-1.5 w-full overflow-hidden rounded-full bg-gray-100"><span className="block h-full rounded-full bg-primary-500" style={{ width: `${(row.visits / busiest) * 100}%` }} /></span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="card">
          <div className="card-titlebar"><div><h2 className="font-semibold text-gray-900">Why learners came</h2><p className="text-xs text-gray-500">Kinds of visit over the same two weeks</p></div></div>
          {data.fortnight.by_type.length ? (
            <ul className="divide-y divide-gray-100">
              {data.fortnight.by_type.map((row) => (
                <li key={row.visit_type} className="flex justify-between gap-3 px-6 py-2.5 text-sm"><span className="text-gray-900">{row.visit_type}</span><span className="tabular-nums text-gray-600">{row.visits}</span></li>
              ))}
            </ul>
          ) : <p className="px-6 py-6 text-center text-sm text-gray-500">No visits in the last two weeks.</p>}
        </section>
      </div>
    </div>
  );
}
